import React, { useState, useEffect } from 'react';
import Heading from '@tds/core-heading';
import DimpleDivider from '@tds/core-dimple-divider';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
// import OrderSummary from '../form/orderSummary';
import Modal from '../design/shared/modal';
import { Box } from '../design/css';
import { OrderDetailsBox } from '../defectManagement/css';
import { customStyles, OrderSummaryMain } from './css';
import { getLocale } from '../misc';
import {
  contentfulFormConfig,
  getContentfulData,
  getData
} from '../contentful';
import {
  orderStatus as statusConstants,
  CONSTANT,
  productValuesToBackendKeys,
  keyOrderSummaryForm,
  keyOrderSummary
} from '../utils/constant';
import ContractEmails from '../design/contract-emails';
import EN from '../../EN.json';
import FR from '../../FR.json';

import request, { apiHost, config as axiosConfig } from '../utils/request';
import ViewOrderHeader from './viewOrderHeader';
import OrderHistory from './components/orderHistory';
import Comments from './components/comments';
import ValidateLoader from '../UI/validateLoader';
import OrderAction from './components/orderAction';
import Attachements from './components/attachments';
import OrderButton from './components/orderButton';
import OrderSummary from '../form/orderSummary/fmOrderSummary';

const ViewOrder = ({ orderData, open, setOpen, onSave, fetchData }) => {
  const locale = getLocale();
  const config = { ...contentfulFormConfig, locale };
  const { id: orderId, status: orderStatus } = orderData;
  const { incomplete } = statusConstants;
  const {
    KEYS: {
      ROLES: { ADMIN }
    }
  } = CONSTANT;

  const {
    WLS: {
      WIRELESS_ORDER_DETAILS: {
        actions: { confirm }
      }
    },
    ORDERS: { ORDER_SUMMARY, save }
  } = locale === 'en-US' ? EN : FR;

  const { getProfile } = request;
  const profile = getProfile();
  const { role: { name: roleName = 'none' } = {} } = profile || {};

  const [progressBarSteps, setProgressBarSteps] = useState();
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [isOrderValidating, setIsOrderValidating] = useState(false);
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const [contractEmailPopUp, setContractEmailPopUp] = useState(false);
  const [actionChanged, setActionChanged] = useState(false);
  const [addNewComment, setAddNewComment] = useState(false);
  const [status, setStatus] = useState(orderStatus || '');
  const isStatusChanged =
    status !== orderStatus || status === statusConstants.contractSend;
  const isOrderUpdated = isStatusChanged || comment;

  const closeModal = needToClose => {
    if (isOrderUpdated && !error) {
      if (onSave) {
        onSave({ isUpdated: true, orderId: orderData.orderId });
      }
      if (needToClose) setOpen(!open);
    }
  };

  const onAddComment = async () => {
    setAddingComment(true);
    await axios
      .post(
        `${apiHost}/orders/${orderData.id}/comments`,
        { text: comment },
        axiosConfig()
      )
      .catch(() => {})
      .finally(() => {
        setAddingComment(false);
      });
  };

  const onSaveClick = async (needToClose = true) => {
    if (comment) await onAddComment();
    closeModal(needToClose);
  };

  const updateOrder = ccEmailData => {
    const updateBody = {
      step: status === incomplete ? 1 : undefined
    };

    if (isStatusChanged) updateBody.status = status;
    if (status === statusConstants.contractSend) {
      updateBody.isContractResend = false;
      if (ccEmailData !== null) {
        updateBody.contractEmail = ccEmailData.contractEmail;
        updateBody.contractEmailCC = ccEmailData.contractEmailCC;
        closeModal(true);
      }
    }
    if (status === statusConstants.submitted) {
      updateBody.completedAt = new Date();
    }
    setLoading(true);

    return request.put(`/orders/${orderData.id}`, updateBody).then(() => {
      fetchData();
      if (comment) {
        onAddComment();
      }
      
      setLoading(false);
    });
  };

  const updateProgressBarSteps = (productSelection, steps) => {
    let result = steps.filter(item =>
      [
        'additionalcustomerInformationFM',
        'customerInformationFullyManaged'
      ].includes(item.fields.backendKey)
    );
    if (productSelection && productSelection.length) {
      result = [
        ...result,
        ...steps.filter(stepItem =>
          productSelection.some(
            item =>
              productValuesToBackendKeys[item] === stepItem.fields.backendKey
          )
        )
      ];
    }

    result = [
      ...result,
      ...steps.filter(item =>
        [keyOrderSummaryForm, keyOrderSummary].includes(item.fields.backendKey)
      )
    ];

    setProgressBarSteps(result);
  };

  useEffect(() => {
    getContentfulData(config).then(res => {
      const { formCategories } = getData(res);
      const categoryFM = formCategories.find(
        category => _.get(category, 'fields.name', '') === 'fully managed'
      );

      const fmOrderSummaryFrom = _.get(categoryFM, 'fields.forms', []).find(
        form => _.get(form, 'fields.name', '') === 'Fully Managed Form'
      );

      updateProgressBarSteps(
        orderData.productSelectionFM,
        _.get(fmOrderSummaryFrom, 'fields.steps', [])
      );
    });
  }, []);

  const onClickSave = async (needToClose = true) => {
    if (!isOrderUpdated) return;
    try {
      setIsOrderValidating(true);
      setLoading(true);

      if (isStatusChanged) {
        if (isStatusChanged && status === statusConstants.contractSend) {
          setContractEmailPopUp(true);
        } else {
          await updateOrder();
          if (comment) {
            onAddComment();
          }
          closeModal(needToClose);
        }
      }
      setIsOrderValidating(false);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setIsOrderValidating(false);
      setLoading(false);
    }
  };

  const button = {
    label: save,
    onClick: onClickSave,
    variant: 'purple'
  };

  const buttonSaveComment = {
    label: save,
    onClick: onSaveClick,
    variant: 'purple'
  };

  const onStatusChange = e => {
    setStatus(e.target.value);
    setActionChanged(true);
  };

  useEffect(() => {
    setError(false);
  }, [status, comment]);

  return (
    <div id="modal">
      {loading ? (
        <ValidateLoader message="Loading Content" />
      ) : (
        <Modal
          open={open}
          setOpen={setOpen}
          customStyles={customStyles}
          header={orderData.orderId}
        >
          <OrderDetailsBox>
            <ViewOrderHeader orderData={orderData} />
          </OrderDetailsBox>
          <Heading level="h2" tag="h4">
            {ORDER_SUMMARY}
          </Heading>
          <OrderSummaryMain>
            <OrderSummary stepData={progressBarSteps} orderData={orderData} />
          </OrderSummaryMain>
          <OrderHistory orderId={orderData.id} />
          <Comments
            comment={comment}
            setComment={setComment}
            orderId={orderData.id}
            activeButton={setAddNewComment}
          />
          <Box
            mt={20}
            display="flex"
            justifyContent="center"
            width="100"
            style={{ alignItems: 'center' }}
          >
            <OrderButton
              button={{ ...buttonSaveComment, label: 'Save' }}
              isOrderValidating={addingComment}
              actionChanged={addNewComment}
            />
          </Box>
          <Attachements orderData={orderData} />
          <DimpleDivider />
          {orderStatus !== statusConstants.submitted && (
            <Heading level="h2" tag="h4">
              {'Select action/status'}
            </Heading>
          )}
          <ContractEmails
            signingAuthorityEmail={orderData.email_signingAuthority || ''}
            open={contractEmailPopUp}
            setOpen={setContractEmailPopUp}
            setData={data => {
              updateOrder(data);
            }}
          />
          {orderStatus !== statusConstants.submitted && (
            <OrderAction
              button={button}
              orderStatus={orderData.status}
              orderId={orderData.id}
              isOrderValidating={isOrderValidating}
              onStatusChange={onStatusChange}
              actionChanged={actionChanged}
              status={status}
              isOrderUpdated={isOrderUpdated}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

ViewOrder.propTypes = {
  orderData: PropTypes.shape({
    orderId: PropTypes.string,
    id: PropTypes.number,
    status: PropTypes.string,
    productSelectionFM: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onStatusUpdateSuccess: PropTypes.func.isRequired
};
export default ViewOrder;
